import {
  TenderTypes,
  LineItem,
  Purchase,
  Refundable,
  RefundReason
} from '../Types';

export const computeRefundDescription = refundables => {
  const totalDollarsRefunding = refundables
    .filter(r => r.type !== TenderTypes.CREDIT_PM)
    .reduce((a, v) => a + parseFloat(v.amountToRefund), 0);
  const totalDollarsRefundingToAccount = refundables
    .filter(r => r.type === TenderTypes.ACCOUNT_BAL_PM)
    .reduce((a, v) => a + parseFloat(v.amountToRefund), 0);
  const totalCreditsRefunding = refundables
    .filter(r => r.type === TenderTypes.CREDIT_PM)
    .reduce((a, v) => a + parseFloat(v.amountToRefund), 0);
  let description = '';
  if (totalDollarsRefundingToAccount > 0) {
    description += `$${totalDollarsRefundingToAccount.toFixed(2)}`;
  } else if (totalDollarsRefunding === 0 && totalCreditsRefunding === 0) {
    description += 0;
  } else {
    if (totalDollarsRefunding > 0) {
      description += `$${totalDollarsRefunding.toFixed(2)}`;
    }
  }
  if (totalDollarsRefunding > 0 && totalCreditsRefunding > 0) {
    description += ' and ';
  }
  if (totalCreditsRefunding > 0) {
    description += `${totalCreditsRefunding} credit${
      totalCreditsRefunding > 1 ? 's' : ''
    }`;
  }
  return description;
};

const sortByTotalAmount = (a, b) => {
  if (a.totalPurchased > b.totalPurchased) {
    return -1;
  } else if (a.totalPurchased < b.totalPurchased) {
    return 1;
  } else {
    return 0;
  }
};

// get the partial pack dollar amount with 2 decimal precision
export const getBundlePackDollarAmount = (item: LineItem) => {
  // prettier-ignore
  const bundle_count_prorated = item.final_amount * Math.floor(item.count_to_refund * 10000 / item.bundle_count) / 10000;
  // prettier-ignore
  return Math.floor(bundle_count_prorated * 100) / 100; // final precision trim
};

export const isCreditPm = pm => pm?.credits_used > 0;
export const isCashPm = pm => Boolean(pm?.is_cash);
export const isGiftCardPm = pm => Boolean(pm?.gift_card);
export const isAccntBalPm = pm => Boolean(pm?.is_account_bal);
export const is3rdPartyCoupon = pm => Boolean(pm?.third_party_coupon);
export const isPackLineItem = i => i.bundle_count > 1;
export const isRefunded = i => i.refunded;
export const noRefundAvailable = i => i.unavailable_for_refund;
export const isMedical = i => Boolean(i.is_medical);

export const computeRefundables = (
  purchase: Purchase,
  selectedItems: LineItem[],
  refundAllToAccount: boolean
) => {
  // these are buckets where funds or credits will be placed and a refund issued for each in each bucket
  let refundableCardMethods: Refundable[] = [];
  let refundableCashOrCouponMethods: Refundable[] = [];
  const refundableCreditMethods: Refundable[] = [];
  const ppms = purchase?.payment_methods || [];

  // selected line items need to be segmented so different types can be processed indepenently
  const selectedItemsMinuPacks = selectedItems.filter(i => !isPackLineItem(i));
  let refundingTotalDollars = selectedItemsMinuPacks.reduce(
    (a, i) => a + (i?.edit_amount || i?.final_amount), // || is to use edit_amount if user expressed a custom line item refund amount
    0
  );
  const selectedCreditPacks = selectedItems.filter(i => isPackLineItem(i));
  refundingTotalDollars += selectedCreditPacks.reduce(
    (a, i) => a + (i?.edit_amount || getBundlePackDollarAmount(i)), // || is to use edit_amount if user expressed a custom line item refund amount
    0
  );

  // segmenting tender types so we can prioritize which we refund first as line item selections are added
  const cardTenders = ppms.filter(
    pm =>
      !isGiftCardPm(pm) &&
      !isCashPm(pm) &&
      !is3rdPartyCoupon(pm) &&
      !isAccntBalPm(pm)
  );
  const giftCardTenders = ppms.filter(pm => isGiftCardPm(pm));
  const cashTenders = ppms.filter(pm => isCashPm(pm) || isAccntBalPm(pm));
  const thirdParthCouponTenders = ppms.filter(pm => is3rdPartyCoupon(pm));

  if (refundAllToAccount) {
    refundableCardMethods.push({
      type: TenderTypes.ACCOUNT_BAL_PM,
      description: `Refund to account balance`,
      amountToRefund: refundingTotalDollars
    });
  } else {
    if (cardTenders.length > 0) {
      // add a refundable for each card in the purchase that has money to refund
      cardTenders.forEach(pm => {
        // if there is anything left to refund on this method
        if (pm.amount - pm.refunded > 0) {
          refundableCardMethods.push({
            id: pm.id,
            type: TenderTypes.CARD_PM,
            description: `Refund to ${pm.description}`,
            amountToRefund: undefined, // to be calculated below
            totalPurchased: pm?.amount,
            availableToRefund: refundAllToAccount
              ? 0
              : pm?.amount - pm?.refunded
          });
        }
      });
    }

    if (giftCardTenders.length > 0) {
      // add a refundable for each gift card in the purchase that has money to refund
      giftCardTenders.forEach(pm => {
        // if there is anything left to refund on this method
        if (pm.amount - pm.refunded > 0) {
          refundableCashOrCouponMethods.push({
            id: pm.id,
            type: TenderTypes.GIFT_CARD,
            description: `Refund ${pm.description.toLowerCase()} to account balance`,
            amountToRefund: undefined, // to be calculated below
            totalPurchased: pm?.amount,
            availableToRefund: refundAllToAccount
              ? 0
              : pm?.amount - pm?.refunded
          });
        }
      });
    }

    // add a cash refund row if cash was used
    if (cashTenders.length > 0) {
      refundableCashOrCouponMethods.push({
        id: cashTenders[0].id,
        type: TenderTypes.CASH_PM,
        description: 'Refund to account balance',
        amountToRefund: undefined, // to be calculated below, parseFloat(remaining.toFixed(2)),
        totalPurchased: cashTenders.reduce((a, pm) => a + pm?.amount, 0),
        availableToRefund: cashTenders.reduce(
          (a, pm) => a + pm?.amount - pm?.refunded,
          0
        )
      });
    }

    // add a 3rd party coupon refund row if a 3rd party coupon was used
    if (thirdParthCouponTenders.length > 0) {
      refundableCashOrCouponMethods.push({
        id: thirdParthCouponTenders[0].id,
        type: TenderTypes.THIRD_PARTY_COUPON,
        description: 'Refund coupons to account balance',
        amountToRefund: undefined, // to be calculated below, parseFloat(remaining.toFixed(2)),
        totalPurchased: thirdParthCouponTenders.reduce(
          (a, pm) => a + pm?.amount,
          0
        ),
        availableToRefund: thirdParthCouponTenders.reduce(
          (a, pm) => a + pm?.amount - pm?.refunded,
          0
        ),
        third_party_coupon: thirdParthCouponTenders[0].third_party_coupon
      });
    }

    // determine how much per card pm we are refunding, fill from the top until the dollar refund total is satisfied
    refundableCardMethods = refundableCardMethods.sort(sortByTotalAmount);
    let remaining = refundingTotalDollars;
    refundableCardMethods = refundableCardMethods
      .filter(rm => rm.type === TenderTypes.CARD_PM)
      .map(rm => {
        const amt =
          Math.round(Math.min(rm.availableToRefund, remaining) * 100) / 100;
        remaining -= amt;
        return {
          ...rm,
          amountToRefund: amt
        };
      });

    // determine how much per cash/coupon pm we are refunding, fill from the top until the dollar refund total is satisfied
    refundableCashOrCouponMethods = refundableCashOrCouponMethods
      .filter(
        rm =>
          rm.type === TenderTypes.THIRD_PARTY_COUPON ||
          rm.type === TenderTypes.CASH_PM ||
          rm.type === TenderTypes.GIFT_CARD
      )
      .map(rm => {
        const amt =
          Math.round(Math.min(rm.availableToRefund, remaining) * 100) / 100;
        remaining -= amt;
        return {
          ...rm,
          amountToRefund: amt
        };
      });

    // approximate equals because division with floats can have small inaccuracies but insignifigant for our purposes
    // this amount will be recalculated in the api, this is just to prevent the need for an api round-trip on any ui change
    if (!approximateEqual(remaining, 0)) {
      console.error(
        `Not all dollar amounts for selected items were added to refundable methods!  Remaining amount: ${remaining}`
      );
      // leaving this here for a bit for debugging on non-localhost, will remove before release
    }
  }

  const creditsUsed = purchase?.total_credits_used;
  if (creditsUsed > 0) {
    const refundingTotalCredits = selectedItemsMinuPacks.reduce(
      (a, i) => a + i?.credits_used,
      0
    );
    // add a summary row for credits to refund
    refundableCreditMethods.push({
      id: TenderTypes.CREDIT_PM,
      type: TenderTypes.CREDIT_PM,
      description: 'Refund to Credits',
      amountToRefund: refundingTotalCredits,
      availableToRefund: purchase?.total_credits_used,
      totalPurchased: purchase?.total_credits_used
    });
  }
  // console.log(refundableCardMethods);
  // console.log(refundableCashOrCouponMethods);
  // console.log(refundableCreditMethods);
  return [
    ...refundableCardMethods,
    ...refundableCashOrCouponMethods,
    ...refundableCreditMethods
  ];
};

export const refundReasonList: RefundReason[] = [
  {
    id: 'employee_error',
    value: 'Employee error',
    display: 'Employee Error'
  },
  {
    id: 'change_mind',
    value: 'Client changed mind',
    display: 'Client Changed Mind'
  },
  {
    id: 'double_charged',
    value: 'Double charged',
    display: 'Double Charged'
  },
  {
    id: 'client_satisfaction',
    value: 'Client satisfaction',
    display: 'Client Satisfaction'
  },
  {
    id: 'membership_not_terminated',
    value: 'Membership not Terminated',
    display: 'Membership Not Terminated'
  },
  {
    id: 'other',
    value: 'other',
    display: 'Other'
  }
];

// prettier-ignore
const approximateEqual = (v1, v2, epsilon = 0.01) => Math.abs(v1 - v2) < epsilon;

export const currencyFormat = (num: number) =>
  Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    num
  );
